/**
 * SessionManager Component
 *
 * Displays active review sessions with resume functionality
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  PlayArrow,
  ExpandMore,
  ExpandLess,
  AccessTime,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useActiveSessions } from '@/hooks/useReviewProgress';
import { ReviewSessionSummary } from '@/types/api';

export interface SessionManagerProps {
  chatId?: number;
  onResumeSession?: (session: ReviewSessionSummary) => void;
}

export const SessionManager: React.FC<SessionManagerProps> = ({
  chatId,
  onResumeSession,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { data: sessions, isLoading, error } = useActiveSessions(chatId);

  // Don't render if no active sessions
  if (!isLoading && (!sessions || sessions.length === 0)) {
    return null;
  }

  const handleResumeClick = (session: ReviewSessionSummary) => {
    if (onResumeSession) {
      onResumeSession(session);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        borderLeft: 4,
        borderColor: 'primary.main',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          backgroundColor: 'primary.light',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime sx={{ color: 'primary.dark' }} />
          <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 600 }}>
            Active Review Sessions
          </Typography>
          {sessions && sessions.length > 0 && (
            <>
              <Chip
                label={sessions.length}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              {!isExpanded && sessions.length === 1 && (
                <Typography variant="caption" sx={{ color: 'primary.dark', ml: 1 }}>
                  {sessions[0].progress_percentage.toFixed(0)}% complete
                </Typography>
              )}
            </>
          )}
        </Box>
        <IconButton size="small" sx={{ color: 'primary.dark' }}>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load active sessions: {error.message}
            </Alert>
          )}

          {/* Sessions List */}
          {sessions && sessions.length > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                You have {sessions.length} active review {sessions.length === 1 ? 'session' : 'sessions'}.
                Resume to continue where you left off.
              </Alert>

              <List sx={{ width: '100%' }}>
                {sessions.map((session, index) => (
                  <ListItem
                    key={session.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: index < sessions.length - 1 ? 1 : 0,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Messages {session.start_message_id.toLocaleString()} - {session.end_message_id.toLocaleString()}
                          </Typography>
                          <Chip
                            label={`${session.progress_percentage.toFixed(0)}%`}
                            size="small"
                            color={session.progress_percentage >= 50 ? 'success' : 'default'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {/* Statistics */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<CheckCircle />}
                              label={`${session.accepted_count} accepted`}
                              size="small"
                              variant="outlined"
                              color="success"
                            />
                            <Chip
                              icon={<Cancel />}
                              label={`${session.rejected_count} rejected`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                            <Chip
                              label={`${session.remaining_count} remaining`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>

                          {/* Last Updated */}
                          {session.last_updated && (
                            <Typography variant="caption" color="text.secondary">
                              Last updated: {format(new Date(session.last_updated), 'PPp')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PlayArrow />}
                        onClick={() => handleResumeClick(session)}
                        size="small"
                      >
                        Resume
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SessionManager;
